import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/ui/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { Modal } from '@/components/ui/Modal';
import { useNotificationStore } from '@/stores/notificationStore';
import { flightCostService, fmtCost, type FcAssessment } from '@/services/FlightCostService';
import { QuickEstimateModal } from './components/QuickEstimateModal';
import { SpendingLimitsModal } from './components/SpendingLimitsModal';
import { RunStatusBadge } from './components/RunStatusBadge';
import { ArrowLeft, Plane, Search, Wallet, X } from 'lucide-react';
import { EventsService } from '@/services/EventsService';
import { FlightCostAgentPanel, FLIGHT_COST_AGENT_ENABLED } from './agent/FlightCostAgentPanel';
import { FlightCostAgentLauncher } from './agent/FlightCostAgentLauncher';

/**
 * Flight Cost Estimates — list of assessments.
 * Assessments are created and priced by the "Flight Cost Estimates" agent (the old wizard is hidden).
 */
export function FlightCostEstimator() {
  const navigate = useNavigate();
  // ?event_id= comes from the event page's "Flight Cost Estimates" card: show that event only.
  const [params, setParams] = useSearchParams();
  const eventFilter = params.get('event_id');
  const [eventName, setEventName] = useState<string | null>(null);
  const notifyError = useNotificationStore((s) => s.error);
  const [items, setItems] = useState<FcAssessment[] | null>(null);
  const [query, setQuery] = useState('');
  const [quickOpen, setQuickOpen] = useState(false);
  const [limitsOpen, setLimitsOpen] = useState(false);
  // Agent chat for one event (full page, like the R&P agent).
  const [agentEventId, setAgentEventId] = useState<string | null>(null);

  async function load() {
    try {
      setItems((await flightCostService.list(eventFilter || undefined)).items);
    } catch (e: any) {
      notifyError(e?.message || 'Could not load flight cost estimates');
      setItems([]);
    }
  }
  useEffect(() => { setItems(null); load(); }, [eventFilter]);
  useEffect(() => {
    setEventName(null);
    if (eventFilter) new EventsService().get(eventFilter).then((e: any) => setEventName(e?.name ?? null)).catch(() => setEventName(null));
  }, [eventFilter]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!items || !q) return items || [];
    return items.filter((a) => [a.name, a.event_name, a.event_client_name, ...a.destinations.map((d) => d.label)].some((v) => v?.toLowerCase().includes(q)));
  }, [items, query]);

  if (agentEventId && FLIGHT_COST_AGENT_ENABLED) {
    return <FlightCostAgentPanel eventId={agentEventId} onClose={() => { setAgentEventId(null); load(); }} />;
  }

  return (
    <PageContainer>
      <PageHeader title="Flight Cost Estimates" description="Estimated return airfare per traveller for each destination, priced from live published fares.">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setLimitsOpen(true)} leftIcon={<Wallet className="w-4 h-4" />}>Spending limits</Button>
          <Button variant="secondary" size="sm" onClick={() => setQuickOpen(true)} leftIcon={<Plane className="w-4 h-4" />}>Quick estimate</Button>
        </div>
      </PageHeader>

      {/* FLIGHT_COST_AGENT_SLOT (Moniker Agents session): agent launcher, gated by VITE_FLIGHT_COST_AGENT_ENABLED or ?ai=1. */}
      {eventFilter && (
        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
          <button onClick={() => navigate(`/organization/events/${eventFilter}`)} className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-800">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to event
          </button>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white pl-3 pr-1 py-0.5 text-gray-700">
            Estimates for <b className="font-medium text-gray-900">{eventName || 'this event'}</b>
            <button aria-label="Show all events" title="Show all events" onClick={() => { params.delete('event_id'); setParams(params); }} className="p-0.5 rounded-full text-gray-400 hover:text-gray-800 hover:bg-gray-100"><X className="w-3 h-3" /></button>
          </span>
        </div>
      )}

      {FLIGHT_COST_AGENT_ENABLED && <FlightCostAgentLauncher key={eventFilter || 'all'} onOpen={setAgentEventId} defaultEventId={eventFilter} />}

      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="relative w-72">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search event, client or destination"
            className="w-full h-8 pl-8 pr-3 text-xs rounded-md border border-gray-200 bg-white focus:outline-none focus:ring-1 focus:ring-gray-300" />
        </div>
        {items && <span className="text-xs text-gray-500">{filtered.length} estimate{filtered.length === 1 ? '' : 's'}</span>}
      </div>

      <div className="mt-3 rounded-lg border border-gray-200 bg-white overflow-x-auto">
        {!items ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-500">
            {items.length === 0
              ? (eventFilter ? 'No flight cost estimates for this event yet. Ask the Flight Cost Estimates agent to set one up.' : 'No flight cost estimates yet. Ask the Flight Cost Estimates agent to set one up for an event.')
              : 'No estimates match your search.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                {['Estimate', 'Event', 'Destinations', 'Dates', 'Last run', 'Cost', ''].map((h) => (
                  <th key={h} className="text-left p-2 font-semibold text-[10px] uppercase tracking-wide text-gray-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id} className="group border-b last:border-b-0 hover:bg-gray-100/50 cursor-pointer" onClick={() => navigate(`/tools/flight-cost/${a.id}`)}>
                  <td className="p-2 font-medium text-gray-900">{a.name}</td>
                  <td className="p-2 text-gray-700">
                    <div>{a.event_name}</div>
                    {a.event_client_name && <div className="text-[11px] text-gray-500">{a.event_client_name}</div>}
                  </td>
                  <td className="p-2">
                    <div className="flex flex-wrap gap-1">
                      {a.destinations.length === 0 && <span className="text-xs text-gray-400">None yet</span>}
                      {a.destinations.map((d) => (
                        <Badge key={d.key} variant="outline" className="text-[10px]">{d.label} · {d.airports.map((x) => x.iata).join('/') || '—'}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="p-2 text-xs text-gray-600 whitespace-nowrap">{fmtRange(a.inputs.out_date, a.inputs.back_date)}</td>
                  <td className="p-2 whitespace-nowrap">
                    {a.last_run ? (
                      <div className="flex items-center gap-2">
                        <RunStatusBadge status={a.last_run.status} />
                        <span className="text-[11px] text-gray-500">{fmtWhen(a.last_run.finished_at || a.last_run.created_at)}</span>
                      </div>
                    ) : <span className="text-xs text-gray-400">Not priced</span>}
                  </td>
                  <td className="p-2 text-xs text-gray-600 whitespace-nowrap">{a.last_run ? fmtCost(a.last_run.cost.spent_usd) : '—'}</td>
                  <td className="p-2 text-right">
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/tools/flight-cost/${a.id}`); }}>View</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal isOpen={quickOpen} onClose={() => setQuickOpen(false)} title="Quick estimate" description="Price one route right now (one live search)." size="xl">
        <QuickEstimateModal onClose={() => setQuickOpen(false)} />
      </Modal>
      <SpendingLimitsModal isOpen={limitsOpen} onClose={() => setLimitsOpen(false)} />
    </PageContainer>
  );
}

export function fmtRange(a: string | null, b: string | null) {
  if (!a || !b) return '—';
  const f = (s: string) => new Date(`${s}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return `${f(a)} – ${f(b)}, ${b.slice(0, 4)}`;
}

export function fmtWhen(iso: string | null) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
